import React, {useEffect, useState} from "react";
import "./Transactions.css";
import {
    KeyboardArrowDown,
    KeyboardArrowUp,
    Receipt,
    AccountBalanceWallet,
    Phone as PhoneIcon,
    CalendarToday,
    CreditCard
} from "@material-ui/icons";
import {ApiDetails, KEY} from "../../dummyData";
import {motion, AnimatePresence} from "framer-motion";
import {DataGrid} from "@mui/x-data-grid";
import CryptoJS from "crypto-js";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Badge from "@mui/material/Badge";
import Chip from "@mui/material/Chip";
import {ReceiptLong} from "@mui/icons-material";

export default function TransactionsModern(props) {
    // Decrypt tenant details from localStorage
    let details = CryptoJS.AES.decrypt(
        localStorage.getItem("tenant_details"),
        KEY
    );

    let originalText = details.toString(CryptoJS.enc.Utf8);

    // State declarations
    const [transactions, setTransactions] = useState([]);
    const [isExpanded, setIsExpanded] = useState(false);
    const [loading, setLoading] = useState(true);
    const [totalAmount, setTotalAmount] = useState(0);
    const [recentTransactions, setRecentTransactions] = useState([]);
    const [gridRows, setGridRows] = useState([]);

    useEffect(() => {
        fetchTransactions();
    }, []);

    // Calculate total amount when transactions change
    useEffect(() => {
        if (transactions.length > 0) {
            // Process transactions and assign unique IDs
            const processedRows = transactions.map((transaction, index) => ({
                ...transaction,
                // Create a unique ID using index and any other unique properties if available
                id: `row-${index}-${transaction.date_created?.replace(/\D/g, '') || ''}-${transaction.amount || 0}`
            }));

            setGridRows(processedRows);

            // Other calculations (total amount, recent transactions, etc.)
            const total = transactions.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
            setTotalAmount(total);

            // Get the 3 most recent transactions for the summary
            const recent = [...transactions]
                .sort((a, b) => new Date(b.date_created) - new Date(a.date_created))
                .slice(0, 3);
            setRecentTransactions(recent);
        } else {
            setGridRows([]);
        }
    }, [transactions]);

    // Function to fetch transaction data
    const fetchTransactions = () => {
        setLoading(true);

        fetch(ApiDetails + "pegasus/visionary/tenant/get/tenant/transactions", {
            method: "POST",
            headers:{
                "token": new Map(
                    Object.entries(JSON.parse(originalText))
                ).get("access_token"),
            },
            body: JSON.stringify({
                tenant_id: new Map(
                    Object.entries(JSON.parse(originalText))
                ).get("tenant_id"),
            }),
        })
            .then(async (response) => {
                let data = await response.json();
                if (response.status === 403) {
                    // Session expired - redirect to login
                    localStorage.clear();
                    window.location.href = "/";
                    return;
                }

                if (response.status === 200) {
                    setTransactions(data);
                } else {
                    props.snackBar({
                        text: "Failed fetching transactions.",
                        type: "error",
                    });
                }
                setLoading(false);
            })
            .catch((err) => {
                console.log(err);
                props.snackBar({
                    text: "Something Went Wrong. Call Customer Care For Assistance.",
                    type: "error",
                });
                setLoading(false);
            });
    };

    // Format currency function
    const formatCurrency = (amount) => {
        return parseFloat(amount).toLocaleString("en-US", {
            style: "currency",
            currency: "KES",
        });
    };

    // Format phone number with proper spacing
    const formatPhoneNumber = (phone) => {
        if (!phone) return "";
        const cleaned = phone.toString().replace(/\D/g, "");
        const match = cleaned.match(/^(\d{3})(\d{3})(\d{3})$/);
        if (match) {
            return match[1] + " " + match[2] + " " + match[3];
        }
        return phone;
    };

    // Format transaction ID (mask for security)
    const formatTransactionId = (id) => {
        if (!id) return "—";
        // Show only last 4 characters, mask the rest
        return id.length > 4
            ? "•••" + id.substring(id.length - 4)
            : id;
    };

    // Transaction source labels (transaction_type is a source indicator, not a payment direction)
    const transactionTypeLabels = {
        PLATFORM: "M-Pesa",
        MANUAL: "Manual",
        BANK_RECONCILIATION: "Bank Reconciliation",
        DEPOSIT_AS_INCOME: "Deposit",
    };

    // Get status element for a transaction's source/type
    const getTransactionTypeElement = (transaction) => {
        const rawType = transaction.transaction_type;
        const label = transactionTypeLabels[rawType] || rawType || "Payment";

        return (
            <Chip
                label={label}
                size="small"
                style={{
                    backgroundColor: '#DCFCE7',
                    color: '#065F46',
                    border: 'none',
                    fontWeight: 500,
                    fontSize: '12px'
                }}
            />
        );
    };

    // DataGrid column definitions
    const columns = [
        {
            field: "transaction_id",
            headerName: "Transaction ID",
            headerAlign: "left",
            align: "left",
            flex: 1.5,
            minWidth: 150,
            renderCell: (params) => (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 0'
                }}>
                    <Receipt style={{fontSize: 20, color: '#3A5A78'}}/>
                    <div style={{
                        fontWeight: 500,
                        color: '#3A5A78',
                        fontSize: '14px',
                        fontFamily: 'monospace'
                    }}>
                        {/*{formatTransactionId(params.row.transaction_id)}*/}
                        {params.row.transaction_id}
                    </div>
                </div>
            )
        },
        {
            field: "phone_number",
            headerName: "Phone Number",
            headerAlign: "left",
            align: "left",
            flex: 1.5,
            minWidth: 150,
            renderCell: (params) => (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    <PhoneIcon style={{fontSize: 20, color: '#6B7280'}}/>
                    <div style={{
                        color: '#4B5563',
                        fontSize: '14px',
                        fontFamily: 'monospace'
                    }}>
                        {formatPhoneNumber(params.row.phone_number)}
                    </div>
                </div>
            )
        },
        {
            field: "amount",
            headerName: "Amount",
            headerAlign: "right",
            align: "right",
            flex: 1,
            minWidth: 120,
            renderCell: (params) => (
                <div style={{
                    fontWeight: 600,
                    color: '#065F46',
                    fontSize: '14px'
                }}>
                    {formatCurrency(params.row.amount)}
                </div>
            )
        },
        {
            field: "date_created",
            headerName: "Date",
            headerAlign: "center",
            align: "center",
            flex: 1.5,
            minWidth: 180,
            renderCell: (params) => (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: '#6B7280',
                    fontSize: '13px'
                }}>
                    <CalendarToday style={{fontSize: 16}}/>
                    {params.row.date_created}
                </div>
            )
        },
        {
            field: "type",
            headerName: "Type",
            headerAlign: "center",
            align: "center",
            flex: 1,
            minWidth: 120,
            renderCell: (params) => getTransactionTypeElement(params.row)
        }
    ];

    return (
        <div className="transactions-container" style={{margin: '24px 0'}}>
            {/* Loading Overlay */}
            {loading && (
                <Backdrop
                    sx={{color: '#fff', zIndex: 1000}}
                    open={loading}
                >
                    <CircularProgress color="inherit"/>
                </Backdrop>
            )}

            {/* Transactions Summary Cards - Optional but adds value */}
            {!loading && transactions.length > 0 && (
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '16px',
                    marginBottom: '24px'
                }}>
                    {/* Total Transactions Card */}
                    <div style={{
                        flex: '1',
                        minWidth: '200px',
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        padding: '16px',
                        border: '1px solid #E5E7EB',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '8px'
                        }}>
                            <ReceiptLong style={{color: '#3B82F6', fontSize: 20}}/>
                            <div style={{color: '#6B7280', fontSize: '14px'}}>Total Transactions</div>
                        </div>
                        <div style={{
                            fontSize: '24px',
                            fontWeight: 600,
                            color: '#3A5A78'
                        }}>
                            {transactions.length}
                        </div>
                    </div>

                    {/* Total Amount Card */}
                    <div style={{
                        flex: '1',
                        minWidth: '200px',
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        padding: '16px',
                        border: '1px solid #E5E7EB',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '8px'
                        }}>
                            <CreditCard style={{color: '#10B981', fontSize: 20}}/>
                            <div style={{color: '#6B7280', fontSize: '14px'}}>Total Amount</div>
                        </div>
                        <div style={{
                            fontSize: '24px',
                            fontWeight: 600,
                            color: '#065F46'
                        }}>
                            {formatCurrency(totalAmount)}
                        </div>
                    </div>

                    {/* Latest Transaction Card */}
                    {recentTransactions.length > 0 && (
                        <div style={{
                            flex: '2',
                            minWidth: '300px',
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            padding: '16px',
                            border: '1px solid #E5E7EB',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                marginBottom: '8px'
                            }}>
                                <AccountBalanceWallet style={{color: '#8B5CF6', fontSize: 20}}/>
                                <div style={{color: '#6B7280', fontSize: '14px'}}>Latest Transaction</div>
                            </div>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}>
                                <div style={{fontSize: '14px', color: '#4B5563'}}>
                                    {/*{formatTransactionId(recentTransactions[0]?.transaction_id || '')}*/}
                                    {recentTransactions[0]?.transaction_id || ''}
                                </div>
                                <div style={{
                                    fontSize: '18px',
                                    fontWeight: 600,
                                    color: '#065F46'
                                }}>
                                    {formatCurrency(recentTransactions[0]?.amount || 0)}
                                </div>
                            </div>
                            <div style={{
                                fontSize: '12px',
                                color: '#6B7280',
                                marginTop: '4px'
                            }}>
                                {recentTransactions[0]?.date_created || ''}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Main Transactions Table Card */}
            <div
                style={{
                    borderRadius: '12px',
                    border: '1px solid #E5E7EB',
                    overflow: 'hidden',
                    backgroundColor: 'white',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                }}
            >
                {/* Header with toggle button */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '16px',
                        backgroundColor: '#F8FAFC',
                        borderBottom: '1px solid #E5E7EB',
                        cursor: 'pointer',
                    }}
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                        <Badge
                            badgeContent={transactions.length}
                            color="primary"
                            style={{
                                fontSize: '16px',
                                fontWeight: 600,
                                color: '#3A5A78'
                            }}
                        >
                            <span>VIEW TRANSACTIONS</span>
                        </Badge>
                    </div>

                    {isExpanded ?
                        <KeyboardArrowUp style={{color: '#3A5A78'}}/> :
                        <KeyboardArrowDown style={{color: '#3A5A78'}}/>
                    }
                </div>

                {/* Expandable Content Area */}
                <AnimatePresence>
                    {isExpanded && (
                        <motion.div
                            initial={{height: 0, opacity: 0}}
                            animate={{height: 'auto', opacity: 1}}
                            exit={{height: 0, opacity: 0}}
                            transition={{duration: 0.3}}
                            style={{overflow: 'hidden'}}
                        >
                            <div style={{
                                height: transactions.length > 0 ? 400 : 'auto',
                                width: '100%',
                                padding: '16px'
                            }}>
                                {transactions.length > 0 ? (
                                    <DataGrid
                                        rows={gridRows} // Use the processed rows with unique IDs
                                        columns={columns}
                                        pageSize={5}
                                        rowsPerPageOptions={[5, 10, 20]}
                                        disableSelectionOnClick
                                        // No need for getRowId function as each row now has a unique 'id' property
                                        sx={{
                                            border: 'none',
                                            '& .MuiDataGrid-cell': {
                                                borderBottom: '1px solid #f0f0f0',
                                                padding: '8px 16px',
                                            },
                                            '& .MuiDataGrid-columnHeaders': {
                                                backgroundColor: '#f5f8fa',
                                                borderRadius: '8px 8px 0 0',
                                            },
                                            '& .MuiDataGrid-virtualScroller': {
                                                backgroundColor: 'white',
                                            },
                                            '& .MuiDataGrid-footerContainer': {
                                                borderTop: 'none',
                                                backgroundColor: '#f5f8fa',
                                                borderRadius: '0 0 8px 8px',
                                            },
                                            '& .MuiDataGrid-row:hover': {
                                                backgroundColor: '#f5f8fa',
                                            }
                                        }}
                                    />
                                ) : (
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '200px',
                                        padding: '24px',
                                        backgroundColor: '#f8fafc',
                                        borderRadius: '8px'
                                    }}>
                                        <AccountBalanceWallet style={{
                                            fontSize: '48px',
                                            color: '#3B82F6',
                                            marginBottom: '16px'
                                        }}/>
                                        <div style={{
                                            fontSize: '18px',
                                            fontWeight: 600,
                                            color: '#3A5A78',
                                            marginBottom: '8px'
                                        }}>
                                            No Transactions Found
                                        </div>
                                        <div style={{
                                            fontSize: '14px',
                                            color: '#6B7280',
                                            textAlign: 'center'
                                        }}>
                                            You have not made any transactions yet.
                                        </div>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}